// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

// Admin API client for the GLOBAL, platform-admin-editable Irish tax tables that
// drive the Relationship Cashflow tax engine. Tax law is national, so there is no
// per-brokerage scope. The admin-bff proxies /api/** to the resource-server.
// A year with no DB row falls back to the code default. Edits are audited
// (TAX_TABLE, CRITICAL). See docs/specs/cashflow-tax-modeling-prd.md.

export interface UscBand {
  /** Upper income ceiling this rate applies up to. */
  upTo: number;
  /** Rate as a fraction (0.02 = 2%). */
  rate: number;
}

export interface PensionReliefBand {
  /** Inclusive upper age bound for this relief %. */
  maxAge: number;
  /** Relief as a fraction of (capped) earnings (0.25 = 25%). */
  pct: number;
}

/** Inclusive upper age bound + required ARF minimum drawdown % (fraction). */
export interface ArfDrawdownBand {
  /** Inclusive upper age bound for this drawdown %. */
  maxAge: number;
  /** Minimum drawdown as a fraction of the ARF (0.04 = 4%). */
  pct: number;
}

/** Capital Gains Tax: rates are fractions; limit/exemption are € amounts. */
export interface CapitalGainsTax {
  rate: number;
  entrepreneurReliefRate: number;
  entrepreneurReliefLifetimeLimit: number;
  annualExemption: number;
}

/** Exit tax on life/investment products. */
export interface ExitTax {
  /** Exit tax rate as a fraction. */
  rate: number;
  /** Deemed-disposal period in whole years. */
  deemedDisposalYears: number;
}

/** Capital Acquisitions Tax: rate/reliefPct are fractions; thresholds/exemption € amounts. */
export interface CapitalAcquisitionsTax {
  rate: number;
  groupAThreshold: number;
  groupBThreshold: number;
  groupCThreshold: number;
  smallGiftExemption: number;
  businessReliefPct: number;
  agriReliefPct: number;
}

/** Corporation tax rates (fractions). */
export interface CorporationTax {
  tradingRate: number;
  passiveRate: number;
  closeCompanySurchargeRate: number;
}

/** Pension event tax: lump-sum limits € amounts; rates fractions; factor a plain number. */
export interface PensionEventTax {
  lumpSumTaxFreeLimit: number;
  lumpSumStandardRateBandUpper: number;
  lumpSumStandardRate: number;
  chargeableExcessTaxRate: number;
  arfMinimumDrawdownBands: ArfDrawdownBand[];
  dbValuationFactor: number;
}

/** Mirrors the resource-server IrishTaxTables record. Rates are fractions. */
export interface TaxTables {
  taxYear: number;
  sourceUri: string;
  effectiveDate: string; // ISO yyyy-MM-dd

  standardRate: number;
  higherRate: number;
  standardRateBandSingle: number;
  standardRateBandMarried: number;
  marriedSecondEarnerBandMax: number;
  standardRateBandSingleParent: number;

  personalCreditSingle: number;
  personalCreditMarried: number;
  employeeCredit: number;
  ageCreditSingle: number;
  ageCreditMarried: number;
  ageCreditQualifyingAge: number;

  // Additional credits (€).
  earnedIncomeCredit?: number;
  homeCarerCredit?: number;
  singlePersonChildCarerCredit?: number;
  incapacitatedChildCredit?: number;
  dependentRelativeCredit?: number;

  uscExemptionThreshold: number;
  uscBands: UscBand[];
  uscReducedBands: UscBand[];
  uscReducedAge: number;
  uscReducedIncomeLimit: number;

  prsiRate: number;
  prsiExemptAge: number;

  // Self-employed PRSI & USC.
  selfEmployedPrsiRate?: number;
  uscSelfEmployedSurchargeThreshold?: number;
  uscSelfEmployedSurchargeRate?: number;

  dirtRate: number;

  // Dividend withholding tax (fraction).
  dividendWithholdingTaxRate?: number;

  pensionReliefBands: PensionReliefBand[];
  pensionEarningsCap: number;

  // Nested groups (optional — a payload without them still types).
  capitalGainsTax?: CapitalGainsTax;
  exitTax?: ExitTax;
  capitalAcquisitionsTax?: CapitalAcquisitionsTax;
  corporationTax?: CorporationTax;
  pensionEventTax?: PensionEventTax;
}

export interface TaxYearSummary {
  taxYear: number;
  source: 'DB' | 'DEFAULT';
  enabled: boolean;
}

const base = (jurisdiction: string) => `/api/admin/tax-tables/${jurisdiction}`;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const taxTablesApi = {
  listYears: (jurisdiction = 'IE'): Promise<TaxYearSummary[]> => request(base(jurisdiction)),
  get: (year: number, jurisdiction = 'IE'): Promise<TaxTables> =>
    request(`${base(jurisdiction)}/${year}`),
  save: (year: number, tables: TaxTables, jurisdiction = 'IE'): Promise<TaxTables> =>
    request(`${base(jurisdiction)}/${year}`, { method: 'PUT', body: JSON.stringify(tables) }),
  revertToDefault: (year: number, jurisdiction = 'IE'): Promise<void> =>
    request(`${base(jurisdiction)}/${year}`, { method: 'DELETE' }),
};
